import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    MenuItem,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    categoryLabel,
    CHECK_LABELS,
    CheckName,
    DownReply,
    getQuality,
    QualityReport,
    ReplayGroup,
    Tally,
} from '../quality/qualityApi';
import { pct, RateBar, ShareBars, StatTile, VerdictBar, WeeklyColumns } from '../quality/components/charts';
import { ReplayDialog } from '../replays/components/ReplayDialog';
import { errorMessage } from '../replays/replaysApi';
import { RootState } from '../redux/store';
import { GlowAlert } from '../shared/components/GlowAlert';
import { LoadingButton } from '../shared/components/LoadingButton';
import { Section } from '../shared/components/Section';
import { useSelectedUser } from '../users/hooks/useSelectedUser';

import ReportingPage from './ReportingPage';

const WINDOWS: { days: number; label: string }[] = [
    { days: 7, label: '7 days' },
    { days: 30, label: '30 days' },
    { days: 90, label: '90 days' },
    { days: 0, label: 'All time' },
];

const versionLabel = (version: number | null): string => (version === null ? 'unknown' : `v${version}`);

/** The one-line reading of a group's rating, with the caution when it is too thin to trust. */
const Rating: React.FC<{ row: Tally }> = ({ row }) => (
    <Tooltip
        arrow
        title={
            row.rated === 0
                ? 'No rated replies'
                : `${row.up} up, ${row.down} down. Ranked by ${pct(row.score)}, the lowest rate the data still allows.`
        }
    >
        <Box>
            <RateBar value={row.satisfaction} muted={!row.enough} />
        </Box>
    </Tooltip>
);

const ratingHeader = ['Replies', 'Rated', '👍', '👎', 'Satisfaction', 'Checks failed', 'Used a tool', 'Guard fired'];

const RatingCells: React.FC<{ row: Tally }> = ({ row }) => (
    <>
        <TableCell align="right">{row.replies}</TableCell>
        <TableCell align="right">
            {row.rated}
            {!row.enough && row.rated > 0 && (
                <Tooltip title="Too few rated replies to rank on">
                    <Chip size="small" variant="outlined" label="few" sx={{ ml: 0.5, height: 18 }} />
                </Tooltip>
            )}
        </TableCell>
        <TableCell align="right">{row.up}</TableCell>
        <TableCell align="right">{row.down}</TableCell>
        <TableCell sx={{ minWidth: 170 }}>
            <Rating row={row} />
        </TableCell>
        <TableCell align="right">
            <Tooltip title={`${row.check_failed} of ${row.checked} replies that could be checked`}>
                <span>{pct(row.check_failed_rate)}</span>
            </Tooltip>
        </TableCell>
        <TableCell align="right">{pct(row.tool_call_rate)}</TableCell>
        <TableCell align="right">
            <Tooltip title={row.guard_known ? `${row.guard_fired} of ${row.guard_known} replies since it was recorded` : 'Not recorded before this page existed'}>
                <span>{pct(row.guard_rate)}</span>
            </Tooltip>
        </TableCell>
    </>
);

const RatingTable: React.FC<{ firstColumn: string; rows: { key: string; name: string; tally: Tally }[] }> = ({
    firstColumn,
    rows,
}) => (
    <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>{firstColumn}</TableCell>
                    {ratingHeader.map((name) => (
                        <TableCell key={name} align={name === 'Satisfaction' ? 'left' : 'right'}>
                            {name}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.key} sx={{ opacity: row.tally.enough ? 1 : 0.75 }} data-testid="rating-row">
                        <TableCell sx={{ wordBreak: 'break-all' }}>{row.name}</TableCell>
                        <RatingCells row={row.tally} />
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Box>
);

const causeMatches = (reply: DownReply, cause: string | null): boolean => {
    if (!cause) return true;
    if (cause.startsWith('check:')) return reply.failed_checks.includes(cause.slice(6) as CheckName);
    return (reply.category ?? 'uncategorised') === cause.slice(4);
};

const ReplayRow: React.FC<{ name: string; group: ReplayGroup; note?: string }> = ({ name, group, note }) => (
    <TableRow data-testid="replay-group">
        <TableCell sx={{ wordBreak: 'break-all' }}>
            {name}
            {note && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {note}
                </Typography>
            )}
        </TableCell>
        <TableCell align="right">
            {group.done}/{group.runs}
        </TableCell>
        <TableCell>
            <VerdictBar verdicts={group.verdicts} />
        </TableCell>
        <TableCell align="right">
            <Tooltip title={`${group.check_failed} of ${group.checked} runs that could be checked`}>
                <span>{pct(group.check_failed_rate)}</span>
            </Tooltip>
        </TableCell>
        <TableCell align="right">{group.guard_fired}</TableCell>
        <TableCell align="right">{group.tried_to_write}</TableCell>
        <TableCell align="right">
            {group.avg_duration_ms === null ? '–' : `${(group.avg_duration_ms / 1000).toFixed(1)}s`}
        </TableCell>
        <TableCell>
            {group.original_down.runs === 0 ? (
                '–'
            ) : (
                <Typography variant="body2">
                    {group.original_down.better} better, {group.original_down.worse} worse of {group.original_down.runs};{' '}
                    {group.original_down.clean} pass every check
                </Typography>
            )}
        </TableCell>
    </TableRow>
);

const ReplayTable: React.FC<{ firstColumn: string; children: React.ReactNode }> = ({ firstColumn, children }) => (
    <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>{firstColumn}</TableCell>
                    <TableCell align="right">Finished</TableCell>
                    <TableCell>Verdicts</TableCell>
                    <TableCell align="right">Checks failed</TableCell>
                    <TableCell align="right">Guard fired</TableCell>
                    <TableCell align="right">Tried to write</TableCell>
                    <TableCell align="right">Avg time</TableCell>
                    <TableCell>Of the 👎 replies replayed</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
        </Table>
    </Box>
);

const QualityOverview: React.FC = () => {
    const users = useSelector((state: RootState) => state.users.users);
    const [days, setDays] = useState(30);
    const [userId, setUserId] = useSelectedUser();
    const [report, setReport] = useState<QualityReport | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [cause, setCause] = useState<string | null>(null);
    const [replayOf, setReplayOf] = useState<string | null>(null);

    const load = useCallback(async () => {
        setReport(null);
        setError(null);
        setCause(null);
        try {
            setReport(await getQuality(days, userId));
        } catch (err) {
            setError(errorMessage(err, 'Failed to load the quality report'));
        }
    }, [days, userId]);

    useEffect(() => {
        load();
    }, [load]);

    const downReplies = useMemo(
        () => (report?.causes.replies ?? []).filter((reply) => causeMatches(reply, cause)),
        [report, cause]
    );

    const headline = report?.headline;

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={days}
                    aria-label="Time window"
                    onChange={(_, value: number | null) => value !== null && setDays(value)}
                >
                    {WINDOWS.map((w) => (
                        <ToggleButton key={w.days} value={w.days} sx={{ textTransform: 'none' }}>
                            {w.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <TextField
                    select
                    size="small"
                    label="User"
                    value={users.some((u) => u.id === userId) ? userId : ''}
                    onChange={(e) => setUserId(e.target.value)}
                    sx={{ minWidth: 260 }}
                >
                    <MenuItem value="">Everyone</MenuItem>
                    {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.display_name || u.email || u.id}
                        </MenuItem>
                    ))}
                </TextField>
                <LoadingButton onClick={load} loading={!report && !error}>
                    Refresh
                </LoadingButton>
            </Box>

            {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}
            {!report && !error && <GlowAlert severity="info">Loading the quality report...</GlowAlert>}

            {report && headline && (
                <>
                    {report.window.truncated && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            The window has more replies than the report reads; the newest are shown. Pick a shorter window.
                        </Alert>
                    )}

                    <Section
                        title="Overview"
                        subtitle="Satisfaction is thumbs up / (up + down). Most replies are never rated, so read it with the coverage."
                    >
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }} data-testid="headline">
                            <StatTile label="Replies" value={String(headline.replies)} />
                            <StatTile label="Rated" value={String(headline.rated)} note={`${pct(headline.coverage)} of replies`} />
                            <StatTile
                                label="Satisfaction"
                                value={pct(headline.satisfaction)}
                                note={`${headline.up} up, ${headline.down} down`}
                            />
                            <StatTile
                                label="Failed automatic checks"
                                value={pct(headline.check_failed_rate)}
                                note={`of ${headline.checked} checked replies`}
                            />
                            <StatTile
                                label="Guard had to step in"
                                value={pct(headline.guard_rate)}
                                note={headline.guard_known ? `of ${headline.guard_known} replies since it was recorded` : 'not recorded yet'}
                            />
                            <StatTile label="Messages that failed" value={String(headline.failed_messages)} />
                        </Box>
                        {headline.rated > 0 && headline.rated < 10 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                Only {headline.rated} rated {headline.rated === 1 ? 'reply' : 'replies'}: treat every rate on this page as an anecdote, not a measurement.
                            </Typography>
                        )}
                    </Section>

                    <Section
                        title="Models"
                        subtitle="Ranked by the lowest satisfaction the ratings still allow, so a model with two thumbs-up cannot beat one with ninety of a hundred. Models with few ratings are dimmed and listed last."
                    >
                        {report.by_model.length === 0 ? (
                            <Typography color="text.secondary">No replies in this window.</Typography>
                        ) : (
                            <RatingTable
                                firstColumn="Model"
                                rows={report.by_model.map((row) => ({ key: row.model, name: row.model, tally: row }))}
                            />
                        )}
                    </Section>

                    <Section title="Prompt versions" subtitle="The SYSTEM_PROMPT version in force when each reply was written.">
                        {report.by_prompt_version.length === 0 ? (
                            <Typography color="text.secondary">No replies in this window.</Typography>
                        ) : (
                            <RatingTable
                                firstColumn="SYSTEM_PROMPT"
                                rows={report.by_prompt_version.map((row) => ({
                                    key: String(row.version),
                                    name: versionLabel(row.version),
                                    tally: row,
                                }))}
                            />
                        )}
                        {report.by_model_prompt.length > 1 && (
                            <Accordion disableGutters variant="outlined" sx={{ mt: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="body2">Each model with each prompt version</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <RatingTable
                                        firstColumn="Model · prompt"
                                        rows={report.by_model_prompt.map((row) => ({
                                            key: `${row.model}|${row.version}`,
                                            name: `${row.model} · ${versionLabel(row.version)}`,
                                            tally: row,
                                        }))}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Section>

                    <Section
                        title="Why replies get a thumbs-down"
                        subtitle="From the category the user picked, and from the automatic checks. Click a bar to list those replies."
                    >
                        {report.causes.down_total === 0 ? (
                            <Typography color="text.secondary">No thumbs-down in this window.</Typography>
                        ) : (
                            <>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Category given by the user ({report.causes.down_total} thumbs-down)
                                </Typography>
                                <ShareBars
                                    selected={cause}
                                    onSelect={setCause}
                                    items={report.causes.categories.map((c) => ({
                                        key: `cat:${c.category}`,
                                        label: categoryLabel(c.category),
                                        count: c.count,
                                        share: c.share,
                                    }))}
                                />
                                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                                    Automatic checks: failed among the thumbs-down, against all replies
                                </Typography>
                                <Table size="small" sx={{ maxWidth: 760 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Check</TableCell>
                                            <TableCell align="right">In thumbs-down</TableCell>
                                            <TableCell align="right">In all replies</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {report.causes.checks.map((c) => (
                                            <TableRow key={c.check} selected={cause === `check:${c.check}`}>
                                                <TableCell>{CHECK_LABELS[c.check]}</TableCell>
                                                <TableCell align="right">
                                                    {c.down_failed}/{c.down_evaluated}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {c.all_failed}/{c.all_evaluated}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        disabled={c.down_failed === 0}
                                                        onClick={() => setCause(cause === `check:${c.check}` ? null : `check:${c.check}`)}
                                                    >
                                                        {cause === `check:${c.check}` ? 'Clear' : 'Show'}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 1 }}>
                                    <Typography variant="subtitle2">
                                        {cause ? `${downReplies.length} matching replies` : 'The most recent thumbs-down'}
                                    </Typography>
                                    {cause && (
                                        <Button size="small" onClick={() => setCause(null)}>
                                            Clear filter
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ display: 'grid', gap: 1 }}>
                                    {downReplies.slice(0, 25).map((reply) => (
                                        <Box key={reply.id} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="down-reply">
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
                                                <Chip size="small" label={reply.model} />
                                                <Chip size="small" variant="outlined" label={versionLabel(reply.prompt_version)} />
                                                {reply.category && <Chip size="small" color="error" variant="outlined" label={categoryLabel(reply.category)} />}
                                                {reply.failed_checks.map((name) => (
                                                    <Chip key={name} size="small" color="warning" label={CHECK_LABELS[name]} />
                                                ))}
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                                    {new Date(reply.timestamp).toLocaleString()}
                                                </Typography>
                                                <Button size="small" onClick={() => setReplayOf(reply.id)}>
                                                    Replay
                                                </Button>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Asked: {reply.user_text}
                                            </Typography>
                                            <Typography variant="body2">Replied: {reply.reply}</Typography>
                                            {reply.reason && (
                                                <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                                    “{reply.reason}”
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Section>

                    <Section title="Trend" subtitle="Satisfaction per week, from the rated replies of that week.">
                        {report.trend.length === 0 ? (
                            <Typography color="text.secondary">No rated replies in this window.</Typography>
                        ) : (
                            <WeeklyColumns weeks={report.trend} />
                        )}
                    </Section>

                    <Section
                        title="Replays"
                        subtitle="Runs of past turns with another model or prompt, judged by you and by the automatic checks. Write tools are stubs in a replay."
                    >
                        {report.replays.by_model.length === 0 ? (
                            <Typography color="text.secondary">No replays in this window.</Typography>
                        ) : (
                            <>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    By model
                                </Typography>
                                <ReplayTable firstColumn="Model">
                                    {report.replays.by_model.map((group) => (
                                        <ReplayRow key={group.model} name={group.model} group={group} />
                                    ))}
                                </ReplayTable>
                                {report.replays.batches.length > 0 && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                                            By batch
                                        </Typography>
                                        <ReplayTable firstColumn="Batch">
                                            {report.replays.batches.map((batch) => (
                                                <ReplayRow
                                                    key={batch.id}
                                                    name={`${batch.model} · ${batch.prompt_source} prompts`}
                                                    note={`${new Date(batch.created_at).toLocaleString()}${
                                                        batch.criteria?.feedback ? ` · ${batch.criteria.feedback === 'down' ? '👎' : '👍'} replies` : ''
                                                    }`}
                                                    group={batch}
                                                />
                                            ))}
                                        </ReplayTable>
                                    </>
                                )}
                            </>
                        )}
                    </Section>
                </>
            )}
            {replayOf && <ReplayDialog messageId={replayOf} onClose={() => setReplayOf(null)} />}
        </>
    );
};

/** How the models and prompts fare and why replies get a thumbs-down, plus the rated messages themselves. */
const QualityPage: React.FC = () => {
    const [tab, setTab] = useState<'quality' | 'rated'>('quality');
    const [userId, setUserId] = useSelectedUser();
    return (
        <>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
                <Tab value="quality" label="Quality" />
                <Tab value="rated" label="Rated messages" />
            </Tabs>
            {tab === 'quality' ? <QualityOverview /> : <ReportingPage userId={userId} onSelectUser={setUserId} />}
        </>
    );
};

export default QualityPage;
